import { useEffect, useRef, useState } from 'react';
import { isAxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { trackError } from 'utils/trackError';
import { getApiErrorMessage, parseApiError } from 'utils/apiError';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';

import { ProtectedLayout } from 'hoc';
import { useMe } from 'hooks/auth';
import { useUpdateMe } from 'hooks/user';
import { Button, Form, FormControl, FormField, FormMessage, Input } from 'components';
import ChangePasswordDialog from './ChangePasswordDialog';
import { toast } from 'sonner';
import { ErrorText } from 'styles';
import {
  ButtonContainer,
  ContentSection,
  StyledCard,
  StyledCardContent,
  StyledCardDescription,
  StyledCardHeader,
  StyledCardTitle,
  StyledForm,
  StyledFormItem,
  StyledFormLabel,
} from './profile.styles';

export default function ProfileScreen() {
  const { data: user } = useMe();
  const { mutateAsync: updateMe, isPending: isSaving } = useUpdateMe();
  const { t } = useTranslation();
  const [pwdOpen, setPwdOpen] = useState(false);
  const showPasswordChangeTooltip = Boolean(user && !user.passwordChangeAllowed);

  const firstNameInputRef = useRef<HTMLInputElement>(null);

  const profileSchema = z.object({
    email: z.email(t('valid_email')),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
  });

  const form = useForm<z.infer<typeof profileSchema>>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      email: user?.email ?? '',
      firstName: user?.firstName ?? '',
      lastName: user?.lastName ?? '',
    },
    mode: 'onChange',
  });

  useEffect(() => {
    form.reset({
      email: user?.email ?? '',
      firstName: user?.firstName ?? '',
      lastName: user?.lastName ?? '',
    });
  }, [user, form]);

  useEffect(() => {
    if (firstNameInputRef.current) firstNameInputRef.current.focus();
  }, []);

  const saveProfile = async (data: {
    email: string;
    firstName?: string;
    lastName?: string;
  }): Promise<boolean> => {
    try {
      await updateMe(data);
      toast.success(t('profileUpdated'));
      return true;
    } catch (error) {
      const { status, code } = parseApiError(error);
      trackError(error, {
        context: 'updateProfile',
        isAxiosError: isAxiosError(error),
        status,
        code,
      });

      form.setError('root', {
        message: getApiErrorMessage(error, t),
      });
      return false;
    }
  };

  const onSubmit = form.handleSubmit((data) => {
    void saveProfile(data);
  });

  return (
    <ProtectedLayout title={t('myAccount')} hasBackButton>
      <ContentSection>
        <Form {...form}>
          <StyledCard>
            <StyledCardHeader>
              <StyledCardTitle>{t('profileSectionTitle')}</StyledCardTitle>
              <StyledCardDescription>{t('profileCardDescription')}</StyledCardDescription>
            </StyledCardHeader>
            <StyledCardContent>
              <StyledForm
                onSubmit={(e) => {
                  e.preventDefault();
                  void onSubmit(e);
                }}
              >
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <StyledFormItem>
                      <StyledFormLabel>{t('email')}</StyledFormLabel>
                      <FormControl>
                        <Input {...field} type="email" disabled className="h-9 bg-muted" />
                      </FormControl>
                      <FormMessage />
                    </StyledFormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }) => (
                    <StyledFormItem>
                      <StyledFormLabel>{t('firstname')}</StyledFormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          ref={firstNameInputRef}
                          placeholder="Jane"
                          className="h-9"
                        />
                      </FormControl>
                      <FormMessage />
                    </StyledFormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }) => (
                    <StyledFormItem>
                      <StyledFormLabel>{t('lastname')}</StyledFormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Doe" className="h-9" />
                      </FormControl>
                      <FormMessage />
                    </StyledFormItem>
                  )}
                />

                {form.formState.errors.root?.message && (
                  <ErrorText>{form.formState.errors.root.message}</ErrorText>
                )}

                <ButtonContainer>
                  <Button
                    type="submit"
                    disabled={!form.formState.isValid || isSaving}
                    fullWidth={false}
                  >
                    {isSaving ? t('saving') : t('saveProfile')}
                  </Button>
                </ButtonContainer>
              </StyledForm>
            </StyledCardContent>
          </StyledCard>

          <StyledCard>
            <StyledCardHeader>
              <StyledCardTitle>{t('passwordSectionTitle')}</StyledCardTitle>
              <StyledCardDescription>{t('passwordSectionDescription')}</StyledCardDescription>
            </StyledCardHeader>
            <StyledCardContent style={{ paddingTop: '0.5rem' }}>
              <span
                title={showPasswordChangeTooltip ? t('passwordChangeDisabledEntraId') : undefined}
                className="inline-flex"
              >
                <Button
                  type="button"
                  variant="secondary"
                  disabled={!user?.passwordChangeAllowed}
                  onClick={() => setPwdOpen(true)}
                >
                  {t('changePassword')}
                </Button>
              </span>
            </StyledCardContent>
          </StyledCard>
        </Form>
      </ContentSection>
      <ChangePasswordDialog open={pwdOpen} onOpenChange={setPwdOpen} />
    </ProtectedLayout>
  );
}
